import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Link } from 'react-router-dom';

const COOKIE_CONSENT_KEY = 'rocketvan_cookie_consent';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  // Default ALL cookies to ON - user must manually opt out
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: true,  // Default ON
    marketing: true,  // Default ON
    personalization: true // Default ON
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show modal after user has had time to see the page (3 seconds)
      const timer = setTimeout(() => setIsVisible(true), 3000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const savedPrefs = JSON.parse(consent);
        setPreferences(savedPrefs);
      } catch (e) {
        // Invalid saved data, show modal again after delay
        const timer = setTimeout(() => setIsVisible(true), 3000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setIsVisible(false);
    
    // Enable GA4 tracking
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });
    }
  };

  const handleSavePreferences = () => {
    const prefsWithTimestamp = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefsWithTimestamp));
    setIsVisible(false);
    
    // Update GA4 consent based on preferences
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': preferences.analytics ? 'granted' : 'denied',
        'ad_storage': preferences.marketing ? 'granted' : 'denied'
      });
    }
  };

  const handleClose = () => {
    // Close = Accept All (per user requirement)
    handleAcceptAll();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div 
        className="w-full max-w-lg bg-[#001A47] rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300"
        data-testid="cookie-modal"
      >
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-[#039fff] rounded-full p-3">
              <Cookie className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-wide">COOKIE PREFERENCES</h2>
              <p className="text-[#039fff] text-sm">Your Choices, Your Journey</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          {!showPreferences ? (
            <p className="text-gray-300 text-sm leading-relaxed">
              We use cookies to enhance your browsing experience, provide personalized delivery updates, 
              and analyze our traffic. By clicking "Accept All", you consent to our use of cookies to 
              make your Rocket Van Express experience smoother.
            </p>
          ) : (
            <div className="space-y-4">
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <h3 className="text-white font-semibold text-sm">NECESSARY COOKIES</h3>
                  <p className="text-gray-400 text-xs">Required for the website to function</p>
                </div>
                <Switch 
                  checked={true} 
                  disabled 
                  className="data-[state=checked]:bg-[#039fff]"
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <h3 className="text-white font-semibold text-sm">ANALYTICS COOKIES</h3>
                  <p className="text-gray-400 text-xs">Help us improve our services</p>
                </div>
                <Switch 
                  checked={preferences.analytics}
                  onCheckedChange={(checked) => setPreferences({...preferences, analytics: checked})}
                  className="data-[state=checked]:bg-[#039fff]"
                  data-testid="analytics-toggle"
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <h3 className="text-white font-semibold text-sm">MARKETING COOKIES</h3>
                  <p className="text-gray-400 text-xs">Used for targeted advertising</p>
                </div>
                <Switch 
                  checked={preferences.marketing}
                  onCheckedChange={(checked) => setPreferences({...preferences, marketing: checked})}
                  className="data-[state=checked]:bg-[#039fff]"
                  data-testid="marketing-toggle"
                />
              </div>

              {/* Personalization Cookies */}
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <div>
                  <h3 className="text-white font-semibold text-sm">PERSONALIZATION COOKIES</h3>
                  <p className="text-gray-400 text-xs">Remember your preferences</p>
                </div>
                <Switch 
                  checked={preferences.personalization}
                  onCheckedChange={(checked) => setPreferences({...preferences, personalization: checked})}
                  className="data-[state=checked]:bg-[#039fff]"
                  data-testid="personalization-toggle"
                />
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="px-6 pb-4">
          {!showPreferences ? (
            <div className="flex gap-3">
              <Button 
                onClick={handleAcceptAll}
                className="flex-1 bg-[#039fff] hover:bg-[#0380cc] text-white font-semibold py-6 rounded-full"
                data-testid="accept-all-btn"
              >
                <Check className="mr-2 h-5 w-5" />
                ACCEPT ALL
              </Button>
              <Button
                onClick={handleClose}
                variant="ghost"
                size="icon"
                className="rounded-full bg-white/10 hover:bg-white/20 text-white h-12 w-12"
                data-testid="close-cookie-btn"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button 
                onClick={handleAcceptAll}
                className="flex-1 bg-[#039fff] hover:bg-[#0380cc] text-white font-semibold py-6 rounded-full"
              >
                <Check className="mr-2 h-5 w-5" />
                ACCEPT ALL
              </Button>
              <Button
                onClick={handleClose}
                variant="ghost"
                size="icon"
                className="rounded-full bg-white/10 hover:bg-white/20 text-white h-12 w-12"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          )}
          
          <Button
            onClick={() => {
              if (showPreferences) {
                handleSavePreferences();
              } else {
                setShowPreferences(true);
              }
            }}
            variant="outline"
            className={`w-full mt-3 border-white/30 text-white hover:bg-white/10 font-semibold py-6 rounded-full ${
              showPreferences ? 'flex justify-between' : ''
            }`}
            data-testid="manage-prefs-btn"
          >
            {showPreferences ? (
              <>
                <span className="flex items-center">
                  <Settings className="mr-2 h-5 w-5" />
                  HIDE PREFERENCES
                </span>
                <span 
                  className="bg-[#fb6402] hover:bg-[#e55a02] px-6 py-1 rounded-full text-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSavePreferences();
                  }}
                >
                  SAVE
                </span>
              </>
            ) : (
              <>
                <Settings className="mr-2 h-5 w-5" />
                MANAGE PREFERENCES
              </>
            )}
          </Button>
        </div>

        {/* Footer Links */}
        <div className="px-6 pb-6 flex justify-center gap-4 text-sm">
          <Link to="/privacy" className="text-[#039fff] hover:underline">
            Privacy Policy
          </Link>
          <span className="text-gray-500">|</span>
          <Link to="/cookies" className="text-[#039fff] hover:underline">
            Cookie Policy
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
